import { useState, useRef, useEffect } from 'react';
import { Pencil, Check, X, Trash2 } from 'lucide-react';
import { truncateAddress } from '@/utils/formatters';
import { formatTimeAgo } from '@/utils/formatters';
import type { WithdrawalAddress } from '@/hooks/useWithdrawalAddresses';

interface WithdrawAddressDropdownProps {
  isOpen: boolean;
  addresses: WithdrawalAddress[];
  filterText: string;
  onSelect: (address: string) => void;
  onUpdateLabel: (id: string, label: string) => void;
  onClearHistory: () => void;
}

// Prevent input blur so dropdown clicks register before the blur timeout fires
const preventBlur = (e: React.MouseEvent) => e.preventDefault();

export const WithdrawAddressDropdown = ({
  isOpen,
  addresses,
  filterText,
  onSelect,
  onUpdateLabel,
  onClearHistory,
}: WithdrawAddressDropdownProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [confirmingClear, setConfirmingClear] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);
  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Filter addresses by input text (match against address and label)
  const filtered = filterText.trim()
    ? addresses.filter((a) => {
        const search = filterText.toLowerCase();
        return (
          a.address.toLowerCase().includes(search) ||
          (a.label && a.label.toLowerCase().includes(search))
        );
      })
    : addresses;

  // Reset editing state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setEditingId(null);
      setConfirmingClear(false);
    }
  }, [isOpen]);

  // Focus edit input when entering edit mode
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingId]);

  // Clear the confirm-clear timeout on unmount
  useEffect(() => {
    return () => {
      if (clearTimeoutRef.current) clearTimeout(clearTimeoutRef.current);
    };
  }, []);

  if (!isOpen || filtered.length === 0) return null;

  const handleStartEdit = (e: React.MouseEvent, addr: WithdrawalAddress) => {
    e.stopPropagation();
    setEditingId(addr.id);
    setEditValue(addr.label || '');
  };

  const handleConfirmEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (editingId) {
      // Strip disallowed characters, trim whitespace. Empty string becomes null (removes label).
      const sanitized = editValue.replace(/[^a-zA-Z0-9 \-.]/g, '').trim();
      onUpdateLabel(editingId, sanitized);
      setEditingId(null);
    }
  };

  const handleCancelEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setEditingId(null);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleConfirmEdit(e);
    if (e.key === 'Escape') handleCancelEdit(e);
  };

  const handleClearHistory = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmingClear) {
      onClearHistory();
      setConfirmingClear(false);
    } else {
      setConfirmingClear(true);
      clearTimeoutRef.current = setTimeout(() => setConfirmingClear(false), 3000);
    }
  };

  return (
    <div className="absolute z-50 top-full left-0 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-border/50 bg-card shadow-lg">
      <div className="text-xs text-muted-foreground uppercase tracking-wider px-3 pt-2 pb-1">
        Recent
      </div>

      {filtered.map((addr) => (
        <div key={addr.id}>
          {editingId === addr.id ? (
            <div
              className="w-full flex items-center gap-2 px-3 min-h-[44px]"
              onMouseDown={preventBlur}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={editInputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onMouseDown={preventBlur}
                onKeyDown={handleEditKeyDown}
                maxLength={20}
                autoFocus
                placeholder="e.g. Coinbase, Phantom"
                className="flex-1 min-w-0 bg-background/50 border border-border rounded px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onMouseDown={preventBlur}
                onClick={handleConfirmEdit}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-accent/20 transition-colors flex-shrink-0"
              >
                <Check className="w-3.5 h-3.5 text-primary" />
              </button>
              <button
                type="button"
                onMouseDown={preventBlur}
                onClick={handleCancelEdit}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-accent/20 transition-colors flex-shrink-0"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onMouseDown={preventBlur}
              onClick={() => onSelect(addr.address)}
              className="w-full flex items-center gap-2 px-3 min-h-[44px] transition-colors hover:bg-accent/10 active:bg-accent/20 cursor-pointer"
            >
              <span className="font-mono text-sm text-foreground">
                {truncateAddress(addr.address, 4)}
              </span>
              {addr.label && (
                <span className="text-sm text-muted-foreground truncate min-w-0">
                  {addr.label}
                </span>
              )}
              <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
                {formatTimeAgo(addr.last_used_at)}
              </span>
              <button
                type="button"
                onMouseDown={preventBlur}
                onClick={(e) => handleStartEdit(e, addr)}
                className="p-1.5 rounded hover:bg-muted/50 transition-colors flex-shrink-0 ml-2"
              >
                <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </button>
          )}
        </div>
      ))}

      <div className="border-t border-border/50 px-3 py-2">
        <button
          type="button"
          onMouseDown={preventBlur}
          onClick={handleClearHistory}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="w-3 h-3" />
          {confirmingClear ? 'Tap again to confirm' : 'Clear history'}
        </button>
      </div>
    </div>
  );
};
